/**
 * Main App component
 */

import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ClerkProvider, SignedIn, SignedOut } from '@clerk/clerk-react';
import { AuthBridge } from './components/AuthBridge';
import { InstallBanner } from './components/InstallBanner';
import { initSyncManager } from './lib/sync-manager';

// Routes
import { EditorPage } from './routes/Editor';
import { ProjectsPage } from './routes/Projects';
import { AuthPage } from './routes/Auth';
import { SettingsPage } from './routes/Settings';
import { GalleryPage } from './routes/Gallery';
import { EmbedViewerPage } from './features/embed/EmbedViewer';

// Get Clerk key from environment
const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

// Development mode - skip auth when no Clerk key is configured
const isDevMode = !clerkPubKey;

/** Dev mode app without authentication */
function DevApp() {
  return (
    <BrowserRouter>
      <div className="min-h-screen bg-background">
        <Routes>
          <Route path="/editor/:projectId?" element={<EditorPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/" element={<Navigate to="/editor" replace />} />
          <Route path="*" element={<Navigate to="/editor" replace />} />
        </Routes>
        <InstallBanner />
      </div>
    </BrowserRouter>
  );
}

/** Production app with Clerk authentication */
function ProdApp() {
  // Initialize background sync manager
  useEffect(() => {
    return initSyncManager();
  }, []);

  return (
    <ClerkProvider publishableKey={clerkPubKey!}>
      <AuthBridge />
      <BrowserRouter>
        <div className="min-h-screen bg-background">
          <Routes>
            {/* Public routes */}
            <Route path="/auth/*" element={<AuthPage />} />

            {/* Protected routes */}
            <Route
              path="/editor/:projectId?"
              element={
                <>
                  <SignedIn>
                    <EditorPage />
                  </SignedIn>
                  <SignedOut>
                    <Navigate to="/auth/sign-in" replace />
                  </SignedOut>
                </>
              }
            />

            <Route
              path="/projects"
              element={
                <>
                  <SignedIn>
                    <ProjectsPage />
                  </SignedIn>
                  <SignedOut>
                    <Navigate to="/auth/sign-in" replace />
                  </SignedOut>
                </>
              }
            />

            <Route
              path="/gallery"
              element={
                <>
                  <SignedIn>
                    <GalleryPage />
                  </SignedIn>
                  <SignedOut>
                    <Navigate to="/auth/sign-in" replace />
                  </SignedOut>
                </>
              }
            />

            <Route
              path="/settings"
              element={
                <>
                  <SignedIn>
                    <SettingsPage />
                  </SignedIn>
                  <SignedOut>
                    <Navigate to="/auth/sign-in" replace />
                  </SignedOut>
                </>
              }
            />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/editor" replace />} />
            <Route path="*" element={<Navigate to="/editor" replace />} />
          </Routes>
          <InstallBanner />
        </div>
      </BrowserRouter>
    </ClerkProvider>
  );
}

/** App component — embed route is public (no auth), rest is auth-dependent */
export function App() {
  // Check if this is an embed route — render without Clerk
  if (window.location.pathname.startsWith('/embed/')) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/embed/:token" element={<EmbedViewerPage />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return isDevMode ? <DevApp /> : <ProdApp />;
}

export default App;
